// eslint-disable-next-line
import mapboxgl from '!mapbox-gl'; // eslint-disable-line import/no-webpack-loader-syntax
import formatDateTime from './formatDateTime';
import pluralizeInches from './pluralizeInches';
import formatReportType from './formatReportType';
import states from '../assets/data/states.json';

let mapBearing = -30;
let mapPitch = 60;

// -----------------------------------------------------------------------------
// CREATES POPUP CONTENT

// Formats content for a map popup based on properties associated with the feature
// interaccted with. Also handles removing any visible popups, adding the popup
// to the map and animating the map to the popup location
// -----------------------------------------------------------------------------

function createPopupContent(feature) {
  let content = '';
  content += `<h6>${feature.properties.town}</h6>`;
  content += `<p><strong>${feature.properties.town}</strong> received <strong>${feature.properties.snow} ${pluralizeInches(feature.properties.snow)}</strong> of snow, according to the last report made to the NWS at <strong>${formatDateTime(feature)}</strong>. `;
  content += formatReportType(feature.properties.report_type);
  content += ` The type of report was listed as <strong class="snow__source">${feature.properties.snow_source}</strong>.</p>`;
  return content;
}

function createPopup(map, feature) {
  const snowPopup = document.querySelector('.snow-popup');
  if (snowPopup) {
    snowPopup.remove();
  }

  const popup = new mapboxgl.Popup({
    className: 'snow-popup',
    anchor: 'top',
    closeOnClick: true,
  }).setLngLat([feature.properties.long, feature.properties.lat])
    .setHTML(createPopupContent(feature));

  // add the popup to the map
  popup.addTo(map);

  // animate the map to the coordinates of the feature
  map.flyTo({
    center: [feature.properties.long, feature.properties.lat],
    zoom: 12,
    pitch: mapPitch,
    bearing: mapBearing,
  });

  map.on('movestart', () => popup.remove());
}

// -----------------------------------------------------------------------------
// DISPLAYING POPUPS

// When a feature is clicked on, this function creates the popup, places it accordingly
// and hands the feature off to create the html needed for the content of the popup.
// It then centers the map to that feature
// -----------------------------------------------------------------------------

function displayPopup(map, event) {
  // collect all the features at the point of the event
  const features = map.queryRenderedFeatures(event.point, {});

  // if there are no features at that point, return out of the function
  if (!features.length) { return; }

  // else, set feature to the first feature in the list of features
  const feature = features[0];

  // construct a new mapbox popup, set it's long/lat position to the long/lat
  // of the feature and set it's html to the result of the createPopupContent function
  if (feature.properties.snow) {
    createPopup(map, feature);
  }
}

// -----------------------------------------------------------------------------
// DISPLAYING POINTER WHEN MOUSING OVER FEATURES

// Checks when the user mouses over the map, and if they mouse over a feature with
// a specified property, changes the hand to a pointer. Note, you may need to change
// what property is checked for based on your data
// -----------------------------------------------------------------------------

function updateMapCursor(map, event) {
  // find all features at the point of the event
  const features = map.queryRenderedFeatures(event.point, {});

  // if the first feature in the list of features has the specified property, display a pointer
  // eslint-disable-next-line
  map.getCanvas().style.cursor = (features.length && features[0].properties.snow !== undefined) ? 'pointer' : '';
}

// -----------------------------------------------------------------------------
// FLYING THE MAP TO A SINGLE POINT
// Animates map to a single point with supplied zoom, pitch and bearing
// -----------------------------------------------------------------------------

function mapSetLocation(map, coordinates, zoomLevel) {
  map.flyTo({
    center: coordinates,
    zoom: zoomLevel,
    pitch: mapPitch,
    bearing: mapBearing,
  });
}

// -----------------------------------------------------------------------------
// FLYING THE MAP TO A SET OF POINTS
// Animates map to fit to an array of coordinates with supplied pitch, bearing and padding
// -----------------------------------------------------------------------------

function mapSetBounds(map, bounds, mapWidth) {
  map.fitBounds(bounds, {
    pitch: mapPitch,
    bearing: mapBearing,
    padding: mapWidth < 600 ? 20 : 50,
  });
}

function filterMap(map, data, modifiedData, location, state) {
  let filteredData;
  let filteredModifiedData;

  if (location === 'all') {
    map.getSource('snow-polygons').setData(data);
    map.getSource('snow-points').setData(modifiedData);
  } else {
    filteredData = data.features.filter((f) => (
      f.properties.town.toLowerCase() === location.toLowerCase()
      && f.properties.state === state));

    filteredModifiedData = modifiedData.features.filter((f) => (
      f.properties.town.toLowerCase() === location.toLowerCase()
      && f.properties.state === state));

    const filteredGeoJson = {
      type: 'FeatureCollection',
      features: filteredData,
    };

    const filteredModifiedGeoJson = {
      type: 'FeatureCollection',
      features: filteredModifiedData,
    };

    map.getSource('snow-polygons').setData(filteredGeoJson);
    map.getSource('snow-points').setData(filteredModifiedGeoJson);
  }
}

// -----------------------------------------------------------------------------
// ANIMATING MAP TO A STATE
// When a state is selected from the dropdown, the map animates to that state,
// either as a fit to bounds if the state has multiple locations, or to a single
// point specified in the states file imported above
// -----------------------------------------------------------------------------

function flyToState(map, state, location, data, modifiedData, mapWidth) {
  const stateData = data.features.filter((f) => f.properties.state === state);
  filterMap(map, data, modifiedData, location, state);

  if (stateData.length === 0) {
    return;
  }

  if (stateData.length > 1) {
    const bounds = new mapboxgl.LngLatBounds();
    stateData.forEach((f) => bounds.extend([f.properties.long, f.properties.lat]));
    mapSetBounds(map, bounds, mapWidth);
  } else {
    mapSetLocation(map, [stateData[0].properties.long, stateData[0].properties.lat], 11);
  }
}

// -----------------------------------------------------------------------------
// ANIMATING MAP TO A LOCATION FROM THE TABLE
// When a location is clicked in the table, the map is animated to
// that point
// -----------------------------------------------------------------------------

function flyToLocationFromTable(map, coordinates, feature) {
  mapSetLocation(map, coordinates, 10);
  createPopup(map, feature);
}

// -----------------------------------------------------------------------------
// ANIMATING MAP TO A LOCATION FROM THE SELECT
// When a location is supplied from the select, the map is animated to the coordinates
// of the first feature matching the location name and state. The map's features
// are also filtered to show only locations matching that location name and state.
// If 'all' is selected from the dropdown, the map is animated to fit the bounds
// of all features in that state, and all features are shown.
// -----------------------------------------------------------------------------

function flyToLocationFromSelect(map, data, modifiedData, location, state, mapWidth) {
  if (location === 'all') {
    const locationData = data.features.filter((f) => f.properties.state === state);
    const bounds = new mapboxgl.LngLatBounds();
    locationData.forEach((f) => bounds.extend([f.properties.long, f.properties.lat]));
    mapSetBounds(map, bounds, mapWidth);
  } else {
    const locationData = data.features.filter((f) => f.properties.state === state
    && f.properties.town === location);
    mapSetLocation(map, [locationData[0].properties.long, locationData[0].properties.lat], 10);
  }

  filterMap(map, data, modifiedData, location, state);
}

// -----------------------------------------------------------------------------
// TOGGLING THE MAP BETWEEN 2D AND 3D
// Sets new values for the map's pitch and bearing and shows and hides the
// appropriate layers based on the map view value
// -----------------------------------------------------------------------------

function toggleMapView(map, mapView) {
  mapBearing = mapView === '2d' ? 0 : -30;
  mapPitch = mapView === '2d' ? 0 : 60;
  map.setBearing(mapBearing).setPitch(mapPitch);

  if (mapView === '2d' && map.getLayer('snow-points-layer') !== undefined) {
    map.setLayoutProperty('snow-points-layer', 'visibility', 'visible');
    map.setLayoutProperty('snow-polygon-layer', 'visibility', 'none');
  } else if (mapView === '3d' && map.getLayer('snow-polygon-layer') !== undefined) {
    map.setLayoutProperty('snow-points-layer', 'visibility', 'none');
    map.setLayoutProperty('snow-polygon-layer', 'visibility', 'visible');
  }
}

export default {
  displayPopup,
  updateMapCursor,
  flyToState,
  flyToLocationFromTable,
  flyToLocationFromSelect,
  toggleMapView,
};
